import React, { useRef } from "react";
import { Icon } from "@fluentui/react";
import { ENDPOINTS } from "../Services/ServiceConfig";
import { useCurrentPageContext, EndpointData } from "../hooks/useCurrentPageContext";
import { useChatContextTracker } from "../hooks/useChatContextTracker";
import { ChatMessage, ChatSession } from "../interfaces/copilot-chat.interfaces";
import ChatMessageComponent from "../components/AIPlayground/Components/CopilotChat/ChatMessage";
import ChatInput from "../components/ChatInput";
import AttachedContextDisplay from "../components/AttachedContextDisplay";
import { logEvent } from "../helper/AppInsightsHelper";
import { copilotStreamingEnabled } from "../constants/FeatureConfig.constants";
import { streamCopilotResponse } from "../Utils/copilotStream";
import { appendThoughtToTimeline, applyStepToMessage } from "../Utils/copilotTimeline";

interface CopilotPanelProps {
    initialPrompt?: string | null;
    onPromptProcessed?: () => void;
    onClose?: () => void;
    isQuickAction?: boolean;
    onSessionIdChange?: (sessionId: string) => void;
}

const CopilotPanel: React.FC<CopilotPanelProps> = ({
    initialPrompt,
    onPromptProcessed,
    onClose,
    isQuickAction = false, // Default to false if not provided
    onSessionIdChange
}) => {
    const [input, setInput] = React.useState("");
    const [messages, setMessages] = React.useState<ChatMessage[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [firstMessageSent, setFirstMessageSent] = React.useState(false);
    const [showPrompts, setShowPrompts] = React.useState(false);
    const [expandedStates, setExpandedStates] = React.useState<{ [key: string]: boolean }>({});
    const [sessions, setSessions] = React.useState<ChatSession[]>([]);
    const [currentSessionId, setCurrentSessionId] = React.useState<string>("");
    const [currentSessionName, setCurrentSessionName] = React.useState<string>("");
    const [showSidebar, setShowSidebar] = React.useState(false);
    const [attachedEndpoints, setAttachedEndpoints] = React.useState<EndpointData[]>([]);
    const sessionIdRef = useRef<string>("");
    const messagesRef = React.useRef<HTMLDivElement | null>(null);
    const apiTimeoutRef = React.useRef<number | null>(null);
    const streamAbortRef = React.useRef<AbortController | null>(null);

    // Get current page context
    const pageContext = useCurrentPageContext();
    console.log("[CopilotPanel] pageContext:", pageContext);

    // Track context from previous messages
    const trackedContext = useChatContextTracker(messages);

    // Handle Escape key to close panel
    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && onClose) {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    const prompts = [
        "Summarize the data on this page.",
        "What all data sources are being used here?",
        "What's the latest information?",
    ];
    const BASE_URL = `${ENDPOINTS.BASE_URL}aiplayground`;
    const SCENARIO_ID = 16; // Security360 scenario
    const SCENARIO_NAME = "Security360";
    const ATTACHED_ENDPOINTS_KEY = "copilot-attached-endpoints";

    const handleToggleExpand = (key: string) => {
        setExpandedStates((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    // Helper function to save attachedEndpoints to sessionStorage
    const saveEndpointsToStorage = (endpoints: EndpointData[]) => {
        try {
            sessionStorage.setItem(ATTACHED_ENDPOINTS_KEY, JSON.stringify(endpoints));
        } catch (error) {
            console.error("Failed to save endpoints to sessionStorage:", error);
        }
    };

    // Function to add an individual endpoint to attached endpoints
    const addAttachedEndpoint = React.useCallback((endpoint: EndpointData) => {
        setAttachedEndpoints(prev => {
            // Check if this endpoint is not already attached (avoid duplicates)
            const isDuplicate = prev.some(existing => existing.fullEndpoint === endpoint.fullEndpoint);
            if (!isDuplicate) {
                logEvent("Dataset_Attached", "AI Hub - Copilot Chat", "CopilotPanel", {
                    endpoint: endpoint.fullEndpoint,
                    description: endpoint.dataExtractor?.description?.substring(0, 100)
                });
                const newEndpoints = [...prev, endpoint];
                saveEndpointsToStorage(newEndpoints);
                return newEndpoints;
            }
            return prev;
        });
    }, []);

    // Function to remove a specific attached endpoint
    const removeAttachedEndpoint = (endpointKey: string) => {
        logEvent("Dataset_Removed", "AI Hub - Copilot Chat", "CopilotPanel", {
            endpoint: endpointKey
        });
        setAttachedEndpoints(prev => {
            const newEndpoints = prev.filter(endpoint => endpoint.fullEndpoint !== endpointKey);
            saveEndpointsToStorage(newEndpoints);
            return newEndpoints;
        });
    };

    // Function to clear all attached endpoints
    const clearAllAttachedEndpoints = () => {
        setAttachedEndpoints([]);
        try {
            sessionStorage.removeItem(ATTACHED_ENDPOINTS_KEY);
        } catch (error) {
            console.error("Failed to clear endpoints from sessionStorage:", error);
        }
    };

    const loadSessionHistory = async (sessionId: string) => {
        if (!sessionId) return;

        logEvent("PastChatBrowsed", "AI Hub - Copilot Chat", "CopilotPanel", {
            sessionId: sessionId
        });

        clearAllAttachedEndpoints(); // Clear attached endpoints when loading existing session

        setMessages([]);
        setLoading(true);
        setInput("");
        setFirstMessageSent(true);
        setShowPrompts(false);

        try {
            const res = await fetch(`${BASE_URL}/GetCopilotChatSession?sessionId=${sessionId}`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
            });
            const data = await res.json();

            if (!data.sessionId || data.sessionId === "") {
                console.error("Session not found");
                return;
            }

            sessionIdRef.current = data.sessionId;
            setCurrentSessionId(data.sessionId);
            onSessionIdChange?.(data.sessionId);
            if (data.title) {
                setCurrentSessionName(data.title);
            }

            // Map messages
            const msgs = data.messages.map((m: any) => {
                let messageText = m.text;
                let hasAttachment = false;
                let attachmentData: any = undefined;

                // For user messages, check if it's JSON and extract userPrompt
                if (m.role === "user") {
                    try {
                        const parsedMessage = JSON.parse(m.text);
                        if (parsedMessage.userPrompt) {
                            messageText = parsedMessage.userPrompt;
                            hasAttachment = !!(parsedMessage.description || parsedMessage.groundingInstructions);
                            if (hasAttachment) {
                                attachmentData = {
                                    description: parsedMessage.description,
                                    groundingInstructions: parsedMessage.groundingInstructions,
                                    originalText: m.text
                                };
                            }
                        }
                    } catch (e) {
                        messageText = m.text;
                    }
                }

                return {
                    id: m.id,
                    role: m.role === "user" ? "user" : "ai",
                    text: messageText,
                    images: m.images || [],
                    content: m.content,
                    functionCalls: m.functionCalls,
                    action: m.action,
                    hasAttachment,
                    attachmentData
                };
            });
            setMessages(msgs);
        } catch (err) {
            console.error("Error loading session:", err);
        } finally {
            setLoading(false);
        }
    };

    const startNewChat = () => {
        logEvent("NewChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotPanel", {});
        sessionIdRef.current = "";
        setCurrentSessionId("");
        setCurrentSessionName("");
        setMessages([]);
        setInput("");
        setFirstMessageSent(false);
        setShowPrompts(true);
        setLoading(false);
        clearAllAttachedEndpoints(); // Clear attached endpoints when starting new chat

        // Add "New Chat" placeholder back to sessions if it doesn't exist
        setSessions((prev) => {
            const hasNewChat = prev.some(s => s.isNewChat);
            if (!hasNewChat) {
                const newChatSession = {
                    id: "new-chat",
                    title: "New Chat",
                    messages: [],
                    lastUpdated: new Date().toISOString(),
                    scenarioId: SCENARIO_ID,
                    isNewChat: true
                };
                return [newChatSession, ...prev];
            }
            return prev;
        });
    };

    React.useEffect(() => {
        setInput("");
        setMessages([]);
        setLoading(false);
        setFirstMessageSent(false);
        setShowPrompts(false);
        const t = window.setTimeout(() => setShowPrompts(true), 300);

        // Always start in new chat mode
        setShowPrompts(true);

        // Fetch user's chat sessions
        fetch(`${BASE_URL}/GetUserChatSessions`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        })
            .then((res) => res.json())
            .then((data) => {
                const sorted = data
                    .filter((s: any) => s.sessionId && s.sessionId !== "" && s.scenarioId === SCENARIO_ID)
                    .sort(
                        (a: any, b: any) =>
                            new Date(b.lastUpdatedTime).getTime() -
                            new Date(a.lastUpdatedTime).getTime()
                    )
                    .map((s: any) => ({
                        id: s.sessionId,
                        title: s.sessionName,
                        messages: [],
                        lastUpdated: s.lastUpdatedTime,
                        scenarioId: s.scenarioId,
                    }));

                // Add current new chat to the beginning of sessions list
                const newChatSession = {
                    id: "new-chat",
                    title: "New Chat",
                    messages: [],
                    lastUpdated: new Date().toISOString(),
                    scenarioId: SCENARIO_ID,
                    isNewChat: true
                };

                setSessions([newChatSession, ...sorted]);
            })
            .catch(console.error);

        return () => window.clearTimeout(t);
    }, []);

    React.useEffect(() => {
        if (initialPrompt && !firstMessageSent) {
            // If this is a quick action, automatically attach all available endpoints first
            if (isQuickAction && pageContext?.endpointData) {
                // Clear existing endpoints and add all available ones
                setAttachedEndpoints(pageContext.endpointData);

                // Save to session storage
                saveEndpointsToStorage(pageContext.endpointData);

                // Pass endpoints directly to handleSend to avoid async state issues
                setTimeout(() => {
                    handleSend(initialPrompt, pageContext.endpointData);
                    if (onPromptProcessed) {
                        onPromptProcessed();
                    }
                }, 100);
            } else {
                // Regular prompt without quick action
                setTimeout(() => {
                    handleSend(initialPrompt);
                    if (onPromptProcessed) {
                        onPromptProcessed();
                    }
                }, 100);
            }
        }
    }, [initialPrompt, isQuickAction, pageContext?.endpointData, firstMessageSent]);

    React.useEffect(() => {
        if (firstMessageSent && messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [messages, firstMessageSent]);

    // Build grounding instructions with page context and attached endpoints
    const buildGroundingInstructions = (overrideEndpoints?: EndpointData[]): any => {
        const endpointsToUse = overrideEndpoints || attachedEndpoints;

        if (!pageContext) {
            return {
                contextDescription: "You are a helpful AI assistant for Security360 portal.",
                attachedEndpoints: endpointsToUse
            };
        }

        // Build structured context object using pageContext metadata and user-attached endpoints
        const contextObject = {
            pageName: pageContext.pageName,
            tabName: pageContext.tabName,
            urlFilters: pageContext.urlFilters || {},
            attachedEndpoints: endpointsToUse // Use override endpoints if provided, otherwise use state
        };

        return contextObject;
    };

    const makeErrorMessage = (retryUserText: string): ChatMessage => ({
        role: "ai", text: "", isError: true, retryUserText
    });

    const handleRetry = async (retryUserText: string, errorMessageIndex: number) => {
        setMessages((prev) => prev.filter((_, idx) => idx !== errorMessageIndex));
        setLoading(true);
        const userId = sessionStorage.getItem("userId") || "unknown";
        logEvent("UserMessage_Retry", "AI Hub - Copilot Chat", "CopilotPanel", {
            userId: userId,
            sessionId: sessionIdRef.current
        });

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 300000);

        try {
            let response;

            if (!sessionIdRef.current) {
                const retryJson = {
                    description: "Retry the previous message",
                    groundingInstructions: "User has sent this message previously, now the user is retrying it because some error happened",
                    userPrompt: retryUserText
                };
                response = await fetch(`${BASE_URL}/InitiateCopilotChatSession`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        SessionId: "",
                        Message: JSON.stringify(retryJson),
                        ScenarioId: SCENARIO_ID,
                        ScenarioName: SCENARIO_NAME
                    }),
                    signal: controller.signal,
                });
            } else {
                const retryJson = {
                    description: "Retry the previous message",
                    groundingInstructions: "User has sent this message previously, now the user is retrying it because some error happened",
                    userPrompt: retryUserText
                };
                response = await fetch(`${BASE_URL}/GetChatSessionMessageResponse`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        SessionId: sessionIdRef.current,
                        Message: JSON.stringify(retryJson),
                        ScenarioId: SCENARIO_ID,
                        ScenarioName: SCENARIO_NAME
                    }),
                    signal: controller.signal,
                });
            }

            if (!response.ok) {
                logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", {
                    userId: userId,
                    sessionId: sessionIdRef.current,
                    scenarioId: SCENARIO_ID,
                    failureType: "RETRY_FAILURE",
                    errorMessage: `API returned status ${response.status}`
                });
                throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();

            if (!data.messages || data.messages.length === 0) {
                throw new Error("Empty response from API");
            }

            if (data.sessionId && !sessionIdRef.current) {
                sessionIdRef.current = data.sessionId;
                setCurrentSessionId(data.sessionId);
                onSessionIdChange?.(data.sessionId);
            }

            const newMessages: ChatMessage[] = data.messages.map((m: any) => ({
                role: m.role === "user" ? "user" : "ai",
                text: m.text,
                images: m.images || [],
                content: m.content,
                functionCalls: m.functionCalls || [],
            }));

            const hasAgentError = newMessages.some((msg) =>
                msg.text?.includes("AI Agent Response Error")
            );
            if (hasAgentError) {
                logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", {
                    userId: userId,
                    sessionId: sessionIdRef.current,
                    scenarioId: SCENARIO_ID,
                    failureType: "RETRY_FAILURE",
                    errorMessage: "Agent returned error on retry"
                });
                throw new Error("Agent returned an error response");
            }

            setMessages((prev) => [...prev, ...newMessages]);
        } catch (err: any) {
            logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", {
                userId: userId,
                sessionId: sessionIdRef.current,
                scenarioId: SCENARIO_ID,
                failureType: "RETRY_FAILURE",
                errorMessage: err.message || "Unknown error"
            });
            setMessages((prev) => [...prev, makeErrorMessage(retryUserText)]);
        } finally {
            window.clearTimeout(timeoutId);
            setLoading(false);
            if (messagesRef.current) {
                messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
            }
        }
    };

    // ---- Streaming (SSE) helpers ----
    const updateStreamingMessage = (updater: (m: ChatMessage) => ChatMessage) => {
        setMessages((prev) => {
            for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].isStreaming) {
                    const copy = [...prev];
                    copy[i] = updater(copy[i]);
                    return copy;
                }
            }
            return prev;
        });
    };

    const renameStreamingSession = async (sessionId: string, text: string) => {
        let newTitle = text;
        if (text.length > 40) {
            const words = text.split(" ");
            let truncated = "";
            for (const word of words) {
                const potentialLength = truncated ? truncated.length + 1 + word.length : word.length;
                if (potentialLength + 3 <= 40) {
                    truncated += (truncated ? " " : "") + word;
                } else {
                    break;
                }
            }
            newTitle = truncated + "...";
        }
        try {
            await fetch(`${BASE_URL}/RenameChatSession`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                },
                body: JSON.stringify({ sessionId, sessionName: newTitle }),
            });
            setCurrentSessionName(newTitle);
            setSessions((prev) => {
                const filteredPrev = prev.filter((s) => !s.isNewChat);
                const existingIndex = filteredPrev.findIndex((s) => s.id === sessionId);
                if (existingIndex >= 0) {
                    return filteredPrev.map((s) =>
                        s.id === sessionId
                            ? { ...s, title: newTitle, lastUpdated: new Date().toISOString() }
                            : s
                    );
                }
                return [
                    { id: sessionId, title: newTitle, messages: [], lastUpdated: new Date().toISOString(), scenarioId: SCENARIO_ID },
                    ...filteredPrev,
                ];
            });
        } catch (err) {
            console.error("Failed to update session name:", err);
        }
    };

    const runStreamingSend = async (
        text: string,
        endpointsToUse: EndpointData[],
        hasEndpoints: boolean,
        overrideEndpoints: EndpointData[] | undefined,
        userId: string
    ) => {
        const isFirst = !sessionIdRef.current;
        let url: string;
        let body: any;

        if (isFirst) {
            const contextObject = buildGroundingInstructions(overrideEndpoints);
            const description = endpointsToUse.length > 0
                ? endpointsToUse.map((endpoint, index) => `${index + 1}. ${endpoint.dataExtractor.description}`).join("\n")
                : "";
            const firstMessageJson = { description, groundingInstructions: JSON.stringify(contextObject), userPrompt: text };
            url = `${BASE_URL}/InitiateCopilotChatSessionStream`;
            body = { SessionId: "", Message: JSON.stringify(firstMessageJson), ScenarioId: SCENARIO_ID, ScenarioName: SCENARIO_NAME };
        } else {
            let messageToSend = text;
            if (hasEndpoints) {
                const contextObject = buildGroundingInstructions(overrideEndpoints);
                const description = endpointsToUse.length > 0
                    ? endpointsToUse.map((endpoint, index) => `${index + 1}. ${endpoint.dataExtractor.description}`).join("\n")
                    : "";
                messageToSend = JSON.stringify({ description, groundingInstructions: JSON.stringify(contextObject), userPrompt: text });
            }
            url = `${BASE_URL}/GetChatSessionMessageResponseStream`;
            body = { SessionId: sessionIdRef.current, Message: messageToSend, ScenarioId: SCENARIO_ID, ScenarioName: SCENARIO_NAME };
        }

        // Insert the in-progress AI message that fills in live.
        setMessages((prev) => [...prev, { role: "ai", text: "", functionCalls: [], timeline: [], isStreaming: true }]);

        const controller = new AbortController();
        streamAbortRef.current = controller;
        const token = sessionStorage.getItem("token");
        const streamStart = Date.now();
        let firstTokenLogged = false;
        let stepCount = 0;

        try {
            await streamCopilotResponse(url, body, token, {
                onMeta: (sid) => {
                    if (sid && isFirst) {
                        sessionIdRef.current = sid;
                        setCurrentSessionId(sid);
                        onSessionIdChange?.(sid);
                        renameStreamingSession(sid, text);
                    }
                },
                onToken: (tok) => {
                    if (!firstTokenLogged) {
                        firstTokenLogged = true;
                        logEvent("FirstToken_Received", "AI Hub - Copilot Chat", "CopilotPanel", { userId, ttftMs: Date.now() - streamStart });
                    }
                    updateStreamingMessage((m) => ({ ...m, text: m.text + tok }));
                },
                onStep: (step) => {
                    stepCount++;
                    logEvent("ToolStep", "AI Hub - Copilot Chat", "CopilotPanel", { userId, name: step.name, status: step.status });
                    updateStreamingMessage((m) => applyStepToMessage(m, step));
                },
                onThought: (thought, source) => {
                    updateStreamingMessage((m) => ({
                        ...m,
                        thoughts: (m.thoughts || "") + thought,
                        timeline: appendThoughtToTimeline(m.timeline, thought, source),
                    }));
                },
                onDone: (msgs) => {
                    const mapped: ChatMessage[] = (msgs || []).map((m: any) => ({
                        id: m.id,
                        role: m.role === "user" ? "user" : "ai",
                        text: m.text,
                        images: m.images || [],
                        content: m.content,
                        functionCalls: m.functionCalls || [],
                        action: m.action ?? null,
                    }));
                    setMessages((prev) => {
                        const idx = prev.findIndex((mm) => mm.isStreaming);
                        if (idx < 0) return [...prev, ...mapped];
                        const priorThoughts = prev[idx].thoughts;
                        const priorTimeline = prev[idx].timeline;
                        const replacement = mapped.length ? [...mapped] : [{ ...prev[idx], isStreaming: false }];
                        // Attach the streamed thoughts/timeline to the AI message that represents the
                        // response (the last AI message in the done payload), not always the first.
                        let targetIndex = -1;
                        for (let j = replacement.length - 1; j >= 0; j--) {
                            if (replacement[j].role === "ai") {
                                targetIndex = j;
                                break;
                            }
                        }
                        if (targetIndex === -1 && replacement.length) targetIndex = 0;
                        if (targetIndex !== -1) {
                            replacement[targetIndex] = { ...replacement[targetIndex], thoughts: priorThoughts, timeline: priorTimeline };
                        }
                        return [...prev.slice(0, idx), ...replacement, ...prev.slice(idx + 1)];
                    });
                    logEvent("Stream_Completed", "AI Hub - Copilot Chat", "CopilotPanel", { userId, totalMs: Date.now() - streamStart, stepCount });
                },
                onError: (err) => {
                    setMessages((prev) => {
                        const idx = prev.findIndex((mm) => mm.isStreaming);
                        if (idx < 0) return prev;
                        return [...prev.slice(0, idx), makeErrorMessage(text), ...prev.slice(idx + 1)];
                    });
                    logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", { userId, sessionId: sessionIdRef.current, scenarioId: SCENARIO_ID, failureType: "STREAM_ERROR", errorMessage: err });
                },
            }, controller.signal);
        } catch (e: any) {
            if (controller.signal.aborted) {
                updateStreamingMessage((m) => ({ ...m, isStreaming: false, isStopped: true }));
                logEvent("Stream_Cancelled", "AI Hub - Copilot Chat", "CopilotPanel", { userId, sessionId: sessionIdRef.current });
            } else {
                setMessages((prev) => {
                    const idx = prev.findIndex((mm) => mm.isStreaming);
                    if (idx < 0) return prev;
                    return [...prev.slice(0, idx), makeErrorMessage(text), ...prev.slice(idx + 1)];
                });
                logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", { userId, sessionId: sessionIdRef.current, scenarioId: SCENARIO_ID, failureType: "STREAM_EXCEPTION", errorMessage: e?.message || "stream_error" });
            }
        } finally {
            streamAbortRef.current = null;
            setLoading(false);
            if (!overrideEndpoints) {
                clearAllAttachedEndpoints();
            }
            if (messagesRef.current) {
                messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
            }
        }
    };

    const handleSend = async (value?: string, overrideEndpoints?: EndpointData[]) => {
        const text = (value ?? input).trim();
        if (!text) return;
        if (!firstMessageSent) setFirstMessageSent(true);
        setShowPrompts(false);

        // Use override endpoints if provided (for quick actions), otherwise use state
        const endpointsToUse = overrideEndpoints || attachedEndpoints;

        // Check if this is a JSON message with attachments (first message or when endpoints are attached)
        const isFirstMessage = !sessionIdRef.current;
        const hasEndpoints = endpointsToUse.length > 0;
        let hasAttachment = false;
        let attachmentData: any = undefined;

        if (isFirstMessage || hasEndpoints) {
            // Build context object with override endpoints if provided
            const contextObject = buildGroundingInstructions(overrideEndpoints);
            // Build description from attached endpoints descriptions
            const description = endpointsToUse.length > 0
                ? endpointsToUse
                    .map((endpoint, index) => `${index + 1}. ${endpoint.dataExtractor.description}`)
                    .join("\n")
                : ""; // Send blank description when no endpoints are attached
            hasAttachment = true;
            attachmentData = {
                description: description,
                groundingInstructions: JSON.stringify(contextObject),
                originalText: JSON.stringify({
                    description: description,
                    groundingInstructions: JSON.stringify(contextObject),
                    userPrompt: text
                })
            };
        }

        setMessages((prev) => [...prev, { role: "user", text, hasAttachment, attachmentData }]);
        setInput("");
        setLoading(true);

        // Track user message send event
        const userId = sessionStorage.getItem("userId") || "unknown";
        logEvent("UserMessage_Sent", "AI Hub - Copilot Chat", "CopilotPanel", {
            userId: userId
        });

        // Track engaging session when user sends 3rd message
        const currentUserMessageCount = messages.filter((m) => m.role === "user").length + 1;
        if (currentUserMessageCount === 3) {
            logEvent("EngagingSession_Reached", "AI Hub - Copilot Chat", "CopilotPanel", {
                userId: userId,
                sessionId: sessionIdRef.current
            });
        }

        // Streaming (SSE) path — starts rendering the response immediately.
        if (copilotStreamingEnabled) {
            await runStreamingSend(text, endpointsToUse, hasEndpoints, overrideEndpoints, userId);
            return;
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 300000);

        try {
            let response;

            // Check if this is the first message (no session ID yet)
            if (!sessionIdRef.current) {
                // Get the structured context object with override endpoints if provided
                const contextObject = buildGroundingInstructions(overrideEndpoints);

                // Create JSON structure for first message
                const description = endpointsToUse.length > 0
                    ? endpointsToUse
                        .map((endpoint, index) => `${index + 1}. ${endpoint.dataExtractor.description}`)
                        .join("\n")
                    : ""; // Send blank description when no endpoints are attached
                const firstMessageJson = {
                    description: description,
                    groundingInstructions: JSON.stringify(contextObject),
                    userPrompt: text
                };

                // Call InitiateCopilotChatSession for first message
                response = await fetch(`${BASE_URL}/InitiateCopilotChatSession`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        SessionId: "",
                        Message: JSON.stringify(firstMessageJson),
                        ScenarioId: SCENARIO_ID,
                        ScenarioName: SCENARIO_NAME
                    }),
                    signal: controller.signal,
                });
            } else {
                // Call GetChatSessionMessageResponse for subsequent messages
                let messageToSend = text;

                // If there are attached endpoints, send with grounding instructions
                if (hasEndpoints) {
                    const contextObject = buildGroundingInstructions(overrideEndpoints);
                    const description = endpointsToUse.length > 0
                        ? endpointsToUse
                            .map((endpoint, index) => `${index + 1}. ${endpoint.dataExtractor.description}`)
                            .join("\n")
                        : "";

                    const messageJson = {
                        description: description,
                        groundingInstructions: JSON.stringify(contextObject),
                        userPrompt: text
                    };
                    messageToSend = JSON.stringify(messageJson);
                }

                response = await fetch(`${BASE_URL}/GetChatSessionMessageResponse`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        SessionId: sessionIdRef.current,
                        Message: messageToSend,
                        ScenarioId: SCENARIO_ID,
                        ScenarioName: SCENARIO_NAME
                    }),
                    signal: controller.signal,
                });
            }

            if (!response.ok) {
                logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", {
                    userId: userId,
                    sessionId: sessionIdRef.current,
                    scenarioId: SCENARIO_ID,
                    failureType: "API_CALL_FAILURE",
                    errorMessage: `API returned status ${response.status}`
                });
                throw new Error(`API error: ${response.status}`);
            }
            const data = await response.json();

            if (!data.messages || data.messages.length === 0) {
                throw new Error("Empty response from API");
            }

            // Store session ID from first message
            if (data.sessionId && !sessionIdRef.current) {
                sessionIdRef.current = data.sessionId;
                setCurrentSessionId(data.sessionId);
                onSessionIdChange?.(data.sessionId);
                if (data.sessionName) {
                    setCurrentSessionName(data.sessionName);
                }

                // Auto-rename session based on user message (similar to CopilotChat.tsx)
                // Use word-boundary truncation to stay under 40 characters
                let newTitle = text;
                if (text.length > 40) {
                    const words = text.split(" ");
                    let truncated = "";

                    for (const word of words) {
                        // Check if adding this word (plus space if not first word) would exceed the limit
                        const potentialLength = truncated ? truncated.length + 1 + word.length : word.length;
                        if (potentialLength + 3 <= 40) { // +3 for "..."
                            truncated += (truncated ? " " : "") + word;
                        } else {
                            break;
                        }
                    }

                    newTitle = truncated + "...";
                }
                try {
                    await fetch(`${BASE_URL}/RenameChatSession`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                        },
                        body: JSON.stringify({
                            sessionId: data.sessionId,
                            sessionName: newTitle
                        }),
                    });

                    // Update the current session name to reflect the change
                    setCurrentSessionName(newTitle);

                    // Update sessions list to reflect the name change in sidebar
                    setSessions((prev) => {
                        // Remove the "New Chat" placeholder if it exists
                        const filteredPrev = prev.filter(s => !s.isNewChat);

                        const existingSessionIndex = filteredPrev.findIndex(s => s.id === data.sessionId);
                        if (existingSessionIndex >= 0) {
                            // Update existing session
                            return filteredPrev.map((s) =>
                                s.id === data.sessionId
                                    ? {
                                        ...s,
                                        title: newTitle,
                                        lastUpdated: new Date().toISOString(),
                                    }
                                    : s
                            );
                        } else {
                            // Add new session to the beginning of the list
                            return [
                                {
                                    id: data.sessionId,
                                    title: newTitle,
                                    messages: [],
                                    lastUpdated: new Date().toISOString(),
                                    scenarioId: SCENARIO_ID,
                                },
                                ...filteredPrev
                            ];
                        }
                    });
                } catch (err) {
                    console.error("Failed to update session name:", err);
                }
            }

            // Map response messages with functionCalls support
            const newMessages: ChatMessage[] = data.messages.map((m: any) => ({
                role: m.role === "user" ? "user" : "ai",
                text: m.text,
                images: m.images || [],
                content: m.content,
                functionCalls: m.functionCalls || [],
            }));

            // Check for agent errors in the response
            const hasAgentError = newMessages.some((msg) =>
                msg.text?.includes("AI Agent Response Error")
            );
            if (hasAgentError) {
                logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", {
                    userId: userId,
                    sessionId: sessionIdRef.current,
                    scenarioId: SCENARIO_ID,
                    failureType: "AGENT_ERROR",
                    errorMessage: newMessages.find((m) => m.role === "ai")?.text?.substring(0, 200) || "Agent returned error in response"
                });
                throw new Error("Agent returned an error response");
            }

            // Append all new messages to previous list
            setMessages((prev) => [...prev, ...newMessages]);

            // Clear attached endpoints after successful message send (but not for quick actions with override endpoints)
            if (!overrideEndpoints) {
                clearAllAttachedEndpoints();
            }

        } catch (err: any) {
            logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotPanel", {
                userId: userId,
                sessionId: sessionIdRef.current,
                scenarioId: SCENARIO_ID,
                failureType: "API_CALL_FAILURE",
                errorMessage: err.message || "Unknown error"
            });
            setMessages((prev) => [...prev, makeErrorMessage(text)]);
        } finally {
            window.clearTimeout(timeoutId);
            setLoading(false);
            if (messagesRef.current) {
                messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
            }
        }
    };
    const handleStop = () => {
        if (apiTimeoutRef.current) {
            window.clearTimeout(apiTimeoutRef.current);
            apiTimeoutRef.current = null;
        }
        // Abort an in-flight stream (partial answer is kept + persisted by the agent).
        if (streamAbortRef.current) {
            streamAbortRef.current.abort();
            streamAbortRef.current = null;
            return;
        }
        setLoading(false);
        setMessages((prev) => [
            ...prev,
            { role: "ai", text: "[Response stopped by user]" },
        ]);
    };
    return (
        <>
        <style>{`
          .dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #505050;
            display: inline-block;
            transform: translateY(0);
            animation: dot-bounce 1s infinite ease-in-out;
          }
          @keyframes dot-bounce {
            0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
            40% { transform: translateY(-6px); opacity: 1; }
          }
        `}</style>
        <div style={{ display: "flex", height: "100%", maxHeight: "100%", overflow: "hidden" }}>
            {/* Sidebar for chat history */}
            {showSidebar && (
                <div style={{
                    width: "250px",
                    borderRight: "1px solid #e1e4e8",
                    background: "#faf9f8",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%"
                }}>
                    <div style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "8px"
                    }}>
                        {sessions.length === 0 ? (
                            <div style={{
                                padding: "20px",
                                textAlign: "center",
                                color: "#605e5c",
                                fontSize: 12
                            }}>
                                No previous chats
                            </div>
                        ) : (
                            sessions.map((session) => (
                                <button
                                    key={session.id}
                                    onClick={() => {
                                        if (session.isNewChat) {
                                            startNewChat();
                                        } else {
                                            loadSessionHistory(session.id);
                                            setCurrentSessionName(session.title || "Chat Session");
                                        }
                                        setShowSidebar(false);
                                    }}
                                    style={{
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "10px 12px",
                                        background: (currentSessionId === session.id || (session.isNewChat && !currentSessionId)) ? "#e3f2fd" : "#fff",
                                        border: "1px solid #e1e4e8",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        marginBottom: 6,
                                        fontSize: 12,
                                        transition: "all 0.2s"
                                    }}
                                    onMouseEnter={(e) => {
                                        if (currentSessionId !== session.id && !(session.isNewChat && !currentSessionId)) {
                                            e.currentTarget.style.background = "#f6f8fa";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (currentSessionId !== session.id && !(session.isNewChat && !currentSessionId)) {
                                            e.currentTarget.style.background = "#fff";
                                        }
                                    }}
                                >
                                    <div style={{
                                        fontWeight: (currentSessionId === session.id || (session.isNewChat && !currentSessionId)) ? 600 : 400,
                                        color: "#242424",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}>
                                        {session.title}
                                    </div>
                                    <div style={{
                                        fontSize: 10,
                                        color: "#8a8886",
                                        marginTop: 4
                                    }}>
                                        {new Date(session.lastUpdated || "").toLocaleDateString()}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Main chat area */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                {/* Header with menu toggle */}
                <div style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid #e1e4e8",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 12
                }}>
                    <button
                        onClick={() => setShowSidebar(!showSidebar)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: 16,
                            color: "#242424",
                            padding: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 4
                        }}
                        title="Toggle chat history"
                    >
                        <Icon iconName="GlobalNavButton" />
                    </button>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#242424" }}>
                        {currentSessionId ? (currentSessionName || "Chat Session") : "New Chat"}
                    </div>
                    <button
                        onClick={startNewChat}
                        disabled={!currentSessionId}
                        style={{
                            marginLeft: "auto",
                            padding: "6px 12px",
                            background: "transparent",
                            border: currentSessionId ? "1px solid #0078d4" : "1px solid #c8c6c4",
                            color: currentSessionId ? "#0078d4" : "#c8c6c4",
                            borderRadius: 4,
                            cursor: currentSessionId ? "pointer" : "not-allowed",
                            fontSize: 12,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            opacity: currentSessionId ? 1 : 0.5
                        }}
                        title={currentSessionId ? "Start a new chat" : "Already in new chat"}
                    >
                        <Icon iconName="Add" style={{ fontSize: 12 }} />
                        Start new chat
                    </button>
                </div>
                <div style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {!firstMessageSent ? (
                        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        justifyContent: "center",
                        gap: 20,
                        padding: "0 40px 20px 40px",
                        flex: 1,
                        height: "100%"
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginBottom: 12 }}>
                        <span style={{ fontSize: 20, fontWeight: 600, color: "#242424" }}>
                            Hi, how can I assist?
                        </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                        <ChatInput
                            value={input}
                            onChange={setInput}
                            onSend={() => handleSend()}
                            onStop={handleStop}
                            loading={loading}
                            style="welcome"
                            availableEndpoints={pageContext?.endpointData || []}
                            attachedEndpoints={attachedEndpoints}
                            onAttachEndpoint={addAttachedEndpoint}
                            onRemoveEndpoint={removeAttachedEndpoint}
                            trackedContext={trackedContext}
                        />
                    </div>
                    {showPrompts && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", margin: "0 auto", width: "85%" }}>
                            {prompts.map((p, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        logEvent("QuickActionPrompt_Selected", "AI Hub - Copilot Chat", "CopilotPanel", {
                                            promptIndex: idx,
                                            promptText: p.substring(0, 100)
                                        });
                                        const allEndpoints = pageContext?.endpointData || [];
                                        if (allEndpoints.length > 0) {
                                            setAttachedEndpoints(allEndpoints);
                                            saveEndpointsToStorage(allEndpoints);
                                        }
                                        handleSend(p, allEndpoints);
                                    }}
                                    style={{
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "14px 18px",
                                        borderRadius: 16,
                                        border: "1px solid #e1e4e8",
                                        background: "#fff",
                                        cursor: "pointer",
                                        fontSize: 13,
                                        color: "#242424",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                        transition: "all 0.2s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "#f6f8fa";
                                        e.currentTarget.style.borderColor = "#0078d4";
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,120,215,0.15)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "#fff";
                                        e.currentTarget.style.borderColor = "#e1e4e8";
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                                    }}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                        </div>
                    ) : (
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            height: "100%",
                            position: "relative",
                            overflow: "hidden"
                        }}>


                            {/* Fixed context display at top */}
                            {trackedContext.contexts.length > 0 && (
                                <div style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 20,
                                    backgroundColor: "#ffffff",
                                    borderBottom: "1px solid #e1dfdd",
                                    padding: "0 20px"
                                }}>
                                    <AttachedContextDisplay
                                        trackedContext={trackedContext}
                                    />
                                </div>
                            )}

                            <div
                                ref={messagesRef}
                                style={{
                                    padding: "20px",
                                    paddingBottom: (() => {
                                        let basePadding = 90; // Base input area
                                        // Add space for attached endpoints row
                                        if (attachedEndpoints.length > 0) basePadding += 40;
                                        // Add space for available data row
                                        const selectableEndpoints = (pageContext?.endpointData || []).filter(endpoint =>
                                            !attachedEndpoints.some(attached => attached.fullEndpoint === endpoint.fullEndpoint)
                                        );
                                        if (selectableEndpoints.length > 0) basePadding += 40;
                                        return `${basePadding}px`;
                                    })(),
                                    paddingTop: trackedContext.contexts.length > 0 ? "10px" : "20px", // Less padding when context displayed above
                                    overflowY: "auto",
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 10,
                                    minHeight: 0
                                }}
                            >
                                {messages.map((m, i) => (
                                    <ChatMessageComponent
                                        key={i}
                                        message={m}
                                        messageIndex={i}
                                        expandedStates={expandedStates}
                                        onToggleExpand={handleToggleExpand}
                                        onRetry={m.isError && m.retryUserText ? () => handleRetry(m.retryUserText!, i) : undefined}
                                    />
                                ))}
                                {loading && !messages.some((m) => m.isStreaming) && (
                                    <div
                                        style={{
                                            marginTop: 8,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 10,
                                            padding: "10px 14px",
                                            background: "#f8f9fa",
                                            borderRadius: 16,
                                            width: "fit-content",
                                            alignSelf: "flex-start",
                                            fontSize: 13,
                                            fontStyle: "italic",
                                            color: "#605e5c",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
                                        }}
                                    >
                                        <span>Thinking</span>
                                        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                                            <span className="dot" style={{ animationDelay: "0s" }} />
                                            <span className="dot" style={{ animationDelay: "0.12s" }} />
                                            <span className="dot" style={{ animationDelay: "0.24s" }} />
                                        </span>
                                    </div>
                                )}
                            </div>
                            {/* Bottom input pinned */}
                            <div style={{
                                padding: "12px 16px",
                                borderTop: "1px solid #e1e4e8",
                                background: "#fff",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                zIndex: 10
                            }}>
                                <ChatInput
                                    value={input}
                                    onChange={setInput}
                                    onSend={() => handleSend()}
                                    onStop={handleStop}
                                    loading={loading}
                                    style="bottom"
                                    availableEndpoints={pageContext?.endpointData || []}
                                    attachedEndpoints={attachedEndpoints}
                                    onAttachEndpoint={addAttachedEndpoint}
                                    onRemoveEndpoint={removeAttachedEndpoint}
                                    trackedContext={trackedContext}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};
export default CopilotPanel;
